import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PageBody from "../GeneralComponents/PageBody";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import ModalStandardCancel from "../GeneralComponents/ModalStandardCancel";
import GeneralContext from "../GeneralContext/GeneralContext";
import axios from "../../api/axios";
import dayjs from "dayjs";

const ESTADOS = {
  pendiente: { etiqueta: "Pendiente", color: "rgba(0,0,0,0.55)", fondo: "rgba(0,0,0,0.06)" },
  en_curso: { etiqueta: "En curso", color: "#1565c0", fondo: "rgba(21,101,192,0.12)" },
  ok: { etiqueta: "Calculado", color: "#2E7D32", fondo: "rgba(46,125,50,0.12)" },
  error: { etiqueta: "Error", color: "#C62828", fondo: "rgba(198,40,40,0.12)" },
  cancelado: { etiqueta: "Cancelado", color: "rgba(0,0,0,0.55)", fondo: "rgba(0,0,0,0.06)" },
};

const TERMINADOS = ["ok", "error", "cancelado"];

const MAX_WORKERS = [1, 2, 4, 8];

const MAX_DIAS = 92;

const MODOS = {
  faltantes: {
    etiqueta: "Insertar faltantes",
    boton: "Calcular",
    destructivo: false,
    titulo: "Calcular estadísticas RBD",
    describir: (objetivo) =>
      `Se calcularán e insertarán únicamente los RBD que aún no tienen registro en stats_rbd_diario para ${objetivo}.`,
  },
  borrar: {
    etiqueta: "Borrar datos",
    boton: "Borrar",
    destructivo: true,
    titulo: "Borrar estadísticas RBD",
    describir: (objetivo) =>
      `Se eliminarán los registros de stats_rbd_diario para ${objetivo}. No se recalcula nada: lo borrado sólo vuelve con otra corrida.`,
  },
  borrar_insertar: {
    etiqueta: "Borrar e insertar",
    boton: "Recalcular",
    destructivo: true,
    titulo: "Recalcular estadísticas RBD",
    describir: (objetivo) =>
      `Se eliminarán y volverán a calcular todos los registros de stats_rbd_diario para ${objetivo}.`,
  },
};

function numero(valor) {
  if (valor == null) return "—";
  return Number(valor).toLocaleString("es-CL");
}

async function describirError(e) {
  const status = e?.response?.status;
  const data = e?.response?.data;

  let detalle = null;
  try {
    if (data instanceof Blob) {
      detalle = JSON.parse(await data.text())?.detail;
    } else if (data) {
      detalle = data.detail;
    }
  } catch {
    detalle = null;
  }

  if (detalle) return detalle;
  if (status === 403) return "No tienes privilegios para ejecutar el cálculo de estadísticas.";
  if (status === 404) return "BigQuery no devolvió datos de supervisión para el día seleccionado.";
  if (e?.code === "ECONNABORTED")
    return "El cálculo superó el tiempo de espera del navegador; puede haber quedado corriendo en el servidor.";
  return "No se pudo ejecutar el cálculo. Intenta nuevamente.";
}

function EstadoChip({ estado }) {
  const definicion = ESTADOS[estado] ?? ESTADOS.pendiente;
  return (
    <Chip
      size="small"
      label={definicion.etiqueta}
      sx={{
        color: definicion.color,
        background: definicion.fondo,
        fontWeight: 600,
        borderRadius: 0.5,
      }}
    />
  );
}

export default function EstadisticasRbdAdministracion() {
  const { setPageTitle } = useContext(GeneralContext);

  const [fechaIni, setFechaIni] = useState(dayjs().subtract(1, "day").format("YYYY-MM-DD"));
  const [fechaFin, setFechaFin] = useState(dayjs().subtract(1, "day").format("YYYY-MM-DD"));
  const [seleccionados, setSeleccionados] = useState([]);
  const [modo, setModo] = useState("faltantes");
  const [separado, setSeparado] = useState(false);
  const [maxWorkers, setMaxWorkers] = useState(1);

  const [estado, setEstado] = useState(null);
  const [cargandoEstado, setCargandoEstado] = useState(false);

  const [pasos, setPasos] = useState([]);
  const [ejecutando, setEjecutando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [error, setError] = useState(null);

  const detener = useRef(false);

  useEffect(() => {
    setPageTitle?.("ESTADÍSTICAS RBD");
  }, []);

  const consultarEstado = (dia) => {
    if (!dia) return;
    setCargandoEstado(true);
    axios
      .getEstadoStatsRbd({ fecha: dia })
      .then((res) => {
        setEstado(res?.data ?? null);
        setError(null);
      })
      .catch(() => {
        setEstado(null);
        setError(
          "No se pudo consultar la cobertura del día: sin ella tampoco hay lista de operadores, así que solo queda disponible la ejecución para todo el parque."
        );
      })
      .finally(() => setCargandoEstado(false));
  };

  const operadores = useMemo(
    () =>
      (estado?.detalle ?? [])
        .filter((d) => d.id_operador != null)
        .map((d) => ({
          id_operador: d.id_operador,
          nombre_operador: d.nombre_operador,
        })),
    [estado]
  );

  useEffect(() => {
    consultarEstado(fechaIni);
  }, [fechaIni]);

  const dias = useMemo(() => {
    const desde = dayjs(fechaIni);
    const hasta = dayjs(fechaFin);
    if (!fechaIni || !fechaFin || !desde.isValid() || !hasta.isValid()) return [];
    if (hasta.isBefore(desde)) return [];
    const salida = [];
    for (let d = desde; !d.isAfter(hasta) && salida.length < MAX_DIAS; d = d.add(1, "day")) {
      salida.push(d.format("YYYY-MM-DD"));
    }
    return salida;
  }, [fechaIni, fechaFin]);

  const rangoInvalido = Boolean(fechaIni && fechaFin && dayjs(fechaFin).isBefore(dayjs(fechaIni)));
  const rangoRecortado = dias.length === MAX_DIAS && dayjs(fechaFin).diff(dayjs(fechaIni), "day") + 1 > MAX_DIAS;

  const objetivosPaso = useMemo(() => {
    if (!separado) {
      return [
        {
          etiqueta: seleccionados.length
            ? seleccionados.map((o) => o.nombre_operador).join(", ")
            : "Todo el parque",
          opIds: seleccionados.map((o) => o.id_operador),
        },
      ];
    }
    const base = seleccionados.length ? seleccionados : operadores;
    return base.map((o) => ({
      etiqueta: o.nombre_operador ?? `Operador ${o.id_operador}`,
      opIds: [o.id_operador],
    }));
  }, [separado, seleccionados, operadores]);

  const plan = useMemo(
    () => dias.flatMap((dia) => objetivosPaso.map((objetivo) => ({ ...objetivo, fecha: dia }))),
    [dias, objetivosPaso]
  );

  const planValido = plan.length > 0;
  const definicionModo = MODOS[modo] ?? MODOS.faltantes;
  const terminados = pasos.filter((p) => TERMINADOS.includes(p.estado)).length;
  const conError = pasos.filter((p) => p.estado === "error").length;
  const filasTotales = pasos.reduce((total, p) => total + (p.resultado?.filas_upsert ?? 0), 0);
  const borradasTotales = pasos.reduce(
    (total, p) => total + (p.resultado?.filas_borradas ?? 0),
    0
  );

  const actualizarPaso = (indice, cambios) => {
    setPasos((previos) =>
      previos.map((paso, i) => (i === indice ? { ...paso, ...cambios } : paso))
    );
  };

  const ejecutarPasos = async (base, indices) => {
    setPasos(base);
    setEjecutando(true);
    setError(null);
    detener.current = false;

    for (const i of indices) {
      if (detener.current) {
        actualizarPaso(i, { estado: "cancelado" });
        continue;
      }

      actualizarPaso(i, { estado: "en_curso" });

      try {
        const respuesta = await axios.calcularStatsRbd({
          fecha: base[i].fecha,
          modo,
          op_ids: base[i].opIds,
          max_workers: maxWorkers,
        });
        const data = respuesta?.data ?? null;
        const fallo = data?.detalle?.find((d) => d.error);
        actualizarPaso(i, {
          estado: fallo ? "error" : "ok",
          resultado: data,
          mensaje: fallo?.error ?? null,
        });
      } catch (e) {
        actualizarPaso(i, { estado: "error", mensaje: await describirError(e) });
      }
    }

    setEjecutando(false);
    consultarEstado(fechaIni);
  };

  const ejecutar = () => {
    const inicial = plan.map((p) => ({
      ...p,
      estado: "pendiente",
      resultado: null,
      mensaje: null,
    }));
    ejecutarPasos(
      inicial,
      inicial.map((paso, i) => i)
    );
  };

  const reintentarFallidos = () => {
    const indices = [];
    const base = pasos.map((paso, i) => {
      if (paso.estado !== "error") return paso;
      indices.push(i);
      return { ...paso, estado: "pendiente", resultado: null, mensaje: null };
    });
    if (!indices.length) return;
    ejecutarPasos(base, indices);
  };

  const confirmarEjecucion = () => {
    setConfirmando(false);
    ejecutar();
  };

  return (
    <PageBody>
      <Box width="100%">
        <Typography variant="h1" sx={{ textAlign: "center", p: "0.6rem" }}>
          CÁLCULO DE ESTADÍSTICAS RBD
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "center", mb: 3 }}>
          Recalcula las estadísticas diarias por RBD desde los datos de supervisión de
          BigQuery y las carga en stats_rbd_diario, para todo el parque o por operador.
        </Typography>

        <Box
          sx={{
            mb: 3,
            p: 2.5,
            border: "1px solid rgba(0,0,0,0.15)",
            borderRadius: 1,
            background: "#fff",
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: "0 0 170px" }}>
            <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
              Desde:
            </Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={fechaIni}
              disabled={ejecutando}
              onChange={(e) => setFechaIni(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Box sx={{ flex: "0 0 170px" }}>
            <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
              Hasta:
            </Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={fechaFin}
              disabled={ejecutando}
              error={rangoInvalido}
              helperText={rangoInvalido ? "Anterior al inicio" : ""}
              onChange={(e) => setFechaFin(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Box sx={{ flex: "1 1 340px", minWidth: 280 }}>
            <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
              Operadores:
            </Typography>
            <Autocomplete
              multiple
              size="small"
              disabled={ejecutando}
              options={operadores}
              value={seleccionados}
              onChange={(evento, valor) => setSeleccionados(valor)}
              getOptionLabel={(o) => o.nombre_operador ?? `Operador ${o.id_operador}`}
              isOptionEqualToValue={(a, b) => a.id_operador === b.id_operador}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={seleccionados.length ? "" : "Todo el parque"}
                />
              )}
            />
          </Box>

          <Box sx={{ flex: "0 0 260px" }}>
            <Typography variant="body1" sx={{ mb: 0.25, fontWeight: 500 }}>
              Operación:
            </Typography>
            <RadioGroup
              value={modo}
              onChange={(e) => setModo(e.target.value)}
            >
              {Object.entries(MODOS).map(([valor, definicion]) => (
                <FormControlLabel
                  key={valor}
                  value={valor}
                  disabled={ejecutando}
                  control={<Radio size="small" />}
                  label={
                    <Typography
                      variant="body2"
                      sx={{ color: definicion.destructivo ? "#C62828" : "inherit" }}
                    >
                      {definicion.etiqueta}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
          </Box>

          <Box sx={{ flex: "0 0 150px" }}>
            <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
              Hilos:
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={maxWorkers}
              disabled={ejecutando}
              onChange={(e) => setMaxWorkers(Number(e.target.value))}
            >
              {MAX_WORKERS.map((n) => (
                <MenuItem key={n} value={n}>
                  {n === 1 ? "1 (serial)" : n}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ alignSelf: "center", pt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={separado}
                  disabled={ejecutando}
                  onChange={(e) => setSeparado(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  Una llamada por operador
                </Typography>
              }
            />
          </Box>

          <Box sx={{ alignSelf: "flex-end", display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={ejecutando ? <CircularProgress size={16} /> : <PlayArrowIcon />}
              disabled={ejecutando || !planValido}
              onClick={() => setConfirmando(true)}
              sx={{
                background: "#C8D8F0",
                color: "#1565c0",
                boxShadow: "none",
                textTransform: "none",
                "&:hover": { background: "#b3c8e8", boxShadow: "none" },
              }}
            >
              {ejecutando ? "Procesando..." : definicionModo.boton}
            </Button>
            <Button
              startIcon={<StopIcon />}
              disabled={!ejecutando}
              onClick={() => {
                detener.current = true;
              }}
              sx={{ textTransform: "none" }}
            >
              Detener
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              disabled={ejecutando || !conError}
              onClick={reintentarFallidos}
              sx={{ textTransform: "none" }}
            >
              {conError ? `Reintentar ${conError} fallidos` : "Reintentar fallidos"}
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              disabled={ejecutando || cargandoEstado}
              onClick={() => consultarEstado(fechaIni)}
              sx={{ textTransform: "none" }}
            >
              Actualizar estado
            </Button>
          </Box>

          <Box sx={{ flexBasis: "100%" }}>
            <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
              {rangoInvalido
                ? "El día final es anterior al inicial: no hay nada que procesar."
                : modo === "borrar"
                ? `${plan.length} ${plan.length === 1 ? "llamada" : "llamadas"} sobre ${dias.length} ${
                    dias.length === 1 ? "día" : "días"
                  }. El borrado no consulta BigQuery: es sólo un DELETE sobre stats_rbd_diario.`
                : `${plan.length} ${plan.length === 1 ? "llamada" : "llamadas"} secuenciales sobre ${
                    dias.length
                  } ${dias.length === 1 ? "día" : "días"}${
                    separado ? ", una por operador y día" : ", una por día"
                  }. Cada llamada lee de BigQuery sólo los RBD que va a calcular.`}
              {rangoRecortado ? ` El rango se recortó a ${MAX_DIAS} días.` : ""}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              mb: 3,
              border: "1px solid rgba(198, 40, 40, 0.3)",
              background: "rgba(198, 40, 40, 0.08)",
            }}
          >
            <ErrorOutlineIcon sx={{ color: "#C62828" }} fontSize="small" />
            <Typography variant="body2" sx={{ color: "#C62828" }}>
              {error}
            </Typography>
          </Paper>
        )}

        {estado && (
          <PageBgBlock pad="0">
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Cobertura del {estado.fecha}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {numero(estado.rbds_calculados)} de {numero(estado.rbds_parque)} RBD ya
                calculados · {numero(estado.rbds_faltantes)} faltantes
              </Typography>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Operador</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      RBD en parque
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Calculados
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Faltantes
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(estado.detalle ?? []).map((fila) => (
                    <TableRow key={fila.id_operador} hover>
                      <TableCell>
                        {fila.nombre_operador ?? `Operador ${fila.id_operador}`}
                      </TableCell>
                      <TableCell align="right">{numero(fila.rbds_parque)}</TableCell>
                      <TableCell align="right">{numero(fila.rbds_calculados)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: fila.rbds_faltantes ? "#B35300" : "inherit" }}
                      >
                        {numero(fila.rbds_faltantes)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </PageBgBlock>
        )}

        {pasos.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <PageBgBlock pad="0">
              <Box sx={{ px: 2, pt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={(terminados / pasos.length) * 100}
                  sx={{ height: 6, borderRadius: 3 }}
                />
                <Typography variant="body2" sx={{ mt: 1, mb: 1.5 }}>
                  {terminados} de {pasos.length} pasos · {numero(filasTotales)} filas escritas ·{" "}
                  {numero(borradasTotales)} borradas
                  {conError ? ` · ${conError} con error` : ""}
                  {ejecutando ? "" : " · proceso detenido"}
                </Typography>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Día</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Objetivo</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        RBD procesados
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        Filas escritas
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        Filas borradas
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        RBD sin datos
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        Segundos
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pasos.map((paso, indice) => (
                      <React.Fragment key={`${paso.fecha}-${paso.etiqueta}-${indice}`}>
                        <TableRow hover>
                          <TableCell>{paso.fecha}</TableCell>
                          <TableCell>{paso.etiqueta}</TableCell>
                          <TableCell>
                            <EstadoChip estado={paso.estado} />
                          </TableCell>
                          <TableCell align="right">
                            {numero(paso.resultado?.rbds_procesados)}
                          </TableCell>
                          <TableCell align="right">
                            {numero(paso.resultado?.filas_upsert)}
                          </TableCell>
                          <TableCell align="right">
                            {numero(paso.resultado?.filas_borradas)}
                          </TableCell>
                          <TableCell align="right">
                            {numero(paso.resultado?.rbds_fallidos)}
                          </TableCell>
                          <TableCell align="right">
                            {numero(paso.resultado?.segundos_total)}
                          </TableCell>
                        </TableRow>
                        {paso.mensaje && (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ py: 1, border: 0 }}>
                              <Typography variant="body2" sx={{ color: "#C62828" }}>
                                {paso.mensaje}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </PageBgBlock>
          </Box>
        )}

        <ModalStandardCancel
          open={confirmando}
          error={definicionModo.destructivo}
          onClose={() => setConfirmando(false)}
          onClick={confirmarEjecucion}
          btnText={definicionModo.boton}
          title={definicionModo.titulo}
          qst={`${plan.length} ${plan.length === 1 ? "llamada" : "llamadas"} sobre ${
            dias.length === 1 ? `el ${dias[0]}` : `${dias.length} días (${dias[0]} a ${dias[dias.length - 1]})`
          }`}
          msg={definicionModo.describir(
            seleccionados.length
              ? seleccionados.map((o) => o.nombre_operador).join(", ")
              : "todo el parque"
          )}
        />
      </Box>
    </PageBody>
  );
}
